/**
 * brand.ts — identità della lega mostrata nel portale.
 *
 * Una sola fonte per il nome visibile, così la stessa base di codice serve
 * l'istanza di produzione (Lega Puglia) e un'istanza dimostrativa per un altro
 * comitato (es. «Lega Territorio») senza fork e senza toccare il sorgente.
 *
 * Si usa `NEXT_PUBLIC_*` perché il nome compare sia in componenti server
 * (metadata, email) sia in componenti client (header, footer).
 *
 * Il default è il valore storico: **senza variabili il comportamento è
 * invariato**, quindi l'istanza di produzione non cambia.
 */

/** Nome esteso, come compare in titoli, footer e testi istituzionali. */
export const LEAGUE_NAME = process.env.NEXT_PUBLIC_LEAGUE_NAME?.trim() || 'Lega Puglia'

/**
 * Indirizzo della segreteria. In demo conviene puntarlo a un indirizzo neutro:
 * lasciandolo reale, i visitatori della demo scriverebbero alla segreteria vera.
 */
export const LEAGUE_CONTACT_EMAIL =
  process.env.NEXT_PUBLIC_LEAGUE_CONTACT_EMAIL?.trim() || 'segreteria.puglia@pallavolo.me'

/** Numero WhatsApp della segreteria, senza prefisso `+` (formato `wa.me`). */
export const LEAGUE_WHATSAPP = process.env.NEXT_PUBLIC_LEAGUE_WHATSAPP?.trim() || '393938444125'

/**
 * Numero WhatsApp **come va mostrato**: `wa.me/393938444125` nel link non è
 * leggibile, `+39 393 844.4125` sì.
 *
 * Prima questa stringa era scritta a mano in tre componenti (header, footer,
 * pagina partner): su un'istanza di un altro comitato il link era corretto e il
 * numero **mostrato** era ancora quello della Puglia. Ora deriva dallo stesso
 * valore del link, quindi non possono più divergere.
 *
 * Sovrascrivibile con `NEXT_PUBLIC_LEAGUE_WHATSAPP_LABEL` (utile se il numero non
 * ha la forma di un cellulare italiano).
 */
export const LEAGUE_WHATSAPP_LABEL =
  process.env.NEXT_PUBLIC_LEAGUE_WHATSAPP_LABEL?.trim() || formatWhatsappLabel(LEAGUE_WHATSAPP)

/**
 * `393938444125` → `+39 393 844.4125`.
 * Per il numero storico della Puglia l'output è **identico** a quello cablato
 * prima, quindi l'istanza di produzione non cambia: prefisso internazionale, poi
 * `3-3.4` (il taglio delle utenze mobili italiane).
 */
function formatWhatsappLabel(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 12 && digits.startsWith('39')) {
    const n = digits.slice(2)
    return `+39 ${n.slice(0, 3)} ${n.slice(3, 6)}.${n.slice(6)}`
  }
  return `+${digits}`
}

/** Etichetta della segreteria nel footer. */
export const LEAGUE_SECRETARIAT_LABEL = `Segreteria ${LEAGUE_NAME}`

/**
 * Wordmark del comitato nell'header: l'immagine con il **nome** (PUGLIA, VENETO,
 * EMILIA-ROMAGNA).
 *
 * PERCHÉ un'immagine e non testo: è composta con Alphabet Black, il wildstyle
 * di «Alphabet City», che è *free for personal use* — i file dei font non
 * entrano nel portale, quindi le lettere restano pixel (vedi
 * `SiteHeader.tsx` e l'artefatto `2026-09-30-logo-v200w`).
 *
 * PERCHÉ è una variabile: il nome del comitato cambia da istanza a istanza, e
 * prima era cablato su `/puglia-alphabet.png` — così **ogni** portale mostrava
 * «PUGLIA», anche quelli di Veneto ed Emilia-Romagna. Il default è il valore
 * storico, quindi l'istanza pugliese non cambia.
 *
 * Per generare le varianti: `src/15_wordmark_comitati.py` nell'artefatto del logo.
 */
export const LEAGUE_WORDMARK_SRC = process.env.NEXT_PUBLIC_LEAGUE_WORDMARK?.trim() || '/puglia-alphabet.png'

/** Oggetto usato nei `mailto:` delle pagine partner. */
export function partnerMailtoSubject(topic: 'Gold sponsor' | 'Partnership'): string {
  return `${topic} ${LEAGUE_NAME}`
}
